import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { X } from '@phosphor-icons/react'

export default function ManageUserRole() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const memberId = searchParams.get('memberId')

  const [teams, setTeams] = useState([])
  const [groups, setGroups] = useState([])
  const [subgroups, setSubgroups] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [currentHierarchy, setCurrentHierarchy] = useState(null)
  const [newRole, setNewRole] = useState('')
  const [selectedTeam, setSelectedTeam] = useState('')
  const [selectedGroup, setSelectedGroup] = useState('')
  const [selectedSubgroup, setSelectedSubgroup] = useState('')

  useEffect(() => {
    if (!memberId) {
      navigate(-1)
      return
    }
    loadUser()
    loadGroups()
  }, [memberId])

  // Auto-populate hierarchy when selections change
  useEffect(() => {
    if (selectedSubgroup) {
      const subgroup = subgroups.find(s => s.id === selectedSubgroup)
      if (subgroup) {
        setSelectedGroup(subgroup.parent_id)
        const group = groups.find(g => g.id === subgroup.parent_id)
        if (group) {
          setSelectedTeam(group.parent_id)
        }
      }
    } else if (selectedGroup) {
      const group = groups.find(g => g.id === selectedGroup)
      if (group) {
        setSelectedTeam(group.parent_id)
      }
    }
  }, [selectedSubgroup, selectedGroup, groups, subgroups])

  const loadUser = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', memberId)
        .single()

      if (error) throw error
      setSelectedUser(data)
      
      // Set initial role based on current leadership_level
      if (data.leadership_level === 'team') setNewRole('lead')
      else if (data.leadership_level === 'group') setNewRole('head')
      else if (data.leadership_level === 'subgroup') setNewRole('hod')
      else if (data.role === 'member') setNewRole('member')
      else setNewRole('unassigned')
      
      await loadCurrentHierarchy(data.id)
    } catch (error) {
      console.error('Load user error:', error)
      alert('Failed to load user')
      navigate(-1)
    }
  }

  const loadCurrentHierarchy = async (userId) => {
    try {
      const { data: memberships } = await supabase
        .from('group_members')
        .select('group_id')
        .eq('user_id', userId)

      if (!memberships || memberships.length === 0) {
        setCurrentHierarchy({ team: '-', group: '-', subgroup: '-' })
        return
      }

      const { data: { user } } = await supabase.auth.getUser()
      const { data: userData } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', user.id)
        .single()

      const { data: allGroups } = await supabase
        .from('groups')
        .select('*')
        .eq('org_id', userData.org_id)

      const primaryGroupId = memberships[0].group_id
      const primaryGroup = allGroups.find(g => g.id === primaryGroupId)

      if (!primaryGroup) {
        setCurrentHierarchy({ team: '-', group: '-', subgroup: '-' })
        return
      }

      let hierarchy = { team: '-', group: '-', subgroup: '-' }

      if (primaryGroup.depth === 1) {
        hierarchy.team = primaryGroup.name
      } else if (primaryGroup.depth === 2) {
        hierarchy.group = primaryGroup.name
        const parentTeam = allGroups.find(g => g.id === primaryGroup.parent_id)
        hierarchy.team = parentTeam ? parentTeam.name : '-'
      } else if (primaryGroup.depth === 3) {
        hierarchy.subgroup = primaryGroup.name
        const parentGroup = allGroups.find(g => g.id === primaryGroup.parent_id)
        if (parentGroup) {
          hierarchy.group = parentGroup.name
          const parentTeam = allGroups.find(g => g.id === parentGroup.parent_id)
          hierarchy.team = parentTeam ? parentTeam.name : '-'
        }
      }

      setCurrentHierarchy(hierarchy)
    } catch (error) {
      console.error('Load hierarchy error:', error)
      setCurrentHierarchy({ team: '-', group: '-', subgroup: '-' })
    }
  }

  const loadGroups = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { data: userData } = await supabase
        .from('users')
        .select('org_id')
        .eq('id', user.id)
        .single()

      const { data: teamsData } = await supabase
        .from('groups')
        .select('*')
        .eq('org_id', userData.org_id)
        .eq('depth', 1)
        .order('name')

      const { data: groupsData } = await supabase
        .from('groups')
        .select('*')
        .eq('org_id', userData.org_id)
        .eq('depth', 2)
        .order('name')

      const { data: subgroupsData } = await supabase
        .from('groups')
        .select('*')
        .eq('org_id', userData.org_id)
        .eq('depth', 3)
        .order('name')

      setTeams(teamsData || [])
      setGroups(groupsData || [])
      setSubgroups(subgroupsData || [])
    } catch (error) {
      console.error('Load groups error:', error)
    }
  }

  const handleRoleChange = async () => {
    if (!selectedUser) return

    // Validation
    if (newRole === 'lead' && !selectedTeam) {
      alert('Please select a team')
      return
    }
    if (newRole === 'head' && !selectedGroup) {
      alert('Please select a sub team')
      return
    }
    if ((newRole === 'hod' || newRole === 'member') && !selectedSubgroup) {
      alert('Please select a department')
      return
    }

    setLoading(true)
    try {
      let finalGroupId = null
      let leadershipLevel = null

      if (newRole === 'lead') {
        finalGroupId = selectedTeam
        leadershipLevel = 'team'
      } else if (newRole === 'head') {
        finalGroupId = selectedGroup
        leadershipLevel = 'group'
      } else if (newRole === 'hod') {
        finalGroupId = selectedSubgroup
        leadershipLevel = 'subgroup'
      } else if (newRole === 'member') {
        finalGroupId = selectedSubgroup
        leadershipLevel = null
      }

      // Update user record
      const userUpdates = {
        leadership_level: leadershipLevel,
        role: newRole === 'unassigned' ? 'unassigned' : 'member'
      }

      const { error: userError } = await supabase
        .from('users')
        .update(userUpdates)
        .eq('id', selectedUser.id)

      if (userError) throw userError

      // Handle group membership
      if (newRole !== 'unassigned') {
        // Remove existing memberships
        await supabase
          .from('group_members')
          .delete()
          .eq('user_id', selectedUser.id)

        // Add new membership
        const { error: memberError } = await supabase
          .from('group_members')
          .insert({
            user_id: selectedUser.id,
            group_id: finalGroupId
          })

        if (memberError) throw memberError
      } else {
        // Remove all memberships for unassigned
        await supabase
          .from('group_members')
          .delete()
          .eq('user_id', selectedUser.id)
      }

      // Handle QR code
      if (newRole === 'lead') {
        // Remove QR code for team leaders
        await supabase
          .from('users')
          .update({ qr_signature: null })
          .eq('id', selectedUser.id)
      } else if ((newRole === 'head' || newRole === 'hod' || newRole === 'member') && !selectedUser.qr_signature) {
        // Generate QR code if doesn't exist
        const qrSignature = `USER_${selectedUser.id}_${Date.now()}`
        await supabase
          .from('users')
          .update({ qr_signature: qrSignature })
          .eq('id', selectedUser.id)
      }

      alert('Role updated successfully!')
      navigate(-1)
    } catch (error) {
      console.error('Update error:', error)
      alert('Failed to update role: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const getRoleBadge = () => {
    if (selectedUser.leadership_level === 'team') return { text: 'Team leader', color: 'bg-[#eaf5ed] text-[#2b9646] border border-[#bfe0c8]' }
    if (selectedUser.leadership_level === 'group') return { text: 'Group leader', color: 'bg-[#eaf0ff] text-[#2248b2] border border-[#adc2ff]' }
    if (selectedUser.leadership_level === 'subgroup') return { text: 'Subgroup leader', color: 'bg-[#f0eeff] text-[#493caf] border border-[#9589fc]' }
    if (selectedUser.role === 'unassigned') return { text: 'New member', color: 'bg-[#f8fafc] text-[#64748b] border border-[#e2e8f0]' }
    return { text: 'Member', color: 'bg-[#f8fafc] text-[#64748b] border border-[#e2e8f0]' }
  }

  const getSelectedTeamName = () => {
    return teams.find(t => t.id === selectedTeam)?.name || ''
  }

  const getSelectedGroupName = () => {
    return groups.find(g => g.id === selectedGroup)?.name || ''
  }

  const getSelectedSubgroupName = () => {
    return subgroups.find(s => s.id === selectedSubgroup)?.name || ''
  }

  const getRoleDisplayName = () => {
    if (newRole === 'lead') return 'Lead'
    if (newRole === 'head') return 'Head'
    if (newRole === 'hod') return 'H.O.D'
    if (newRole === 'member') return 'Member'
    return 'Unassigned'
  }

  const getAssignmentName = () => {
    if (newRole === 'lead') return getSelectedTeamName()
    if (newRole === 'head') return getSelectedGroupName()
    if (newRole === 'hod' || newRole === 'member') return getSelectedSubgroupName()
    return ''
  }

  if (!selectedUser || !currentHierarchy) {
    return (
      <div className="min-h-screen bg-[#D6e1ff] flex items-center justify-center">
        <p className="text-white text-lg">Loading...</p>
      </div>
    )
  }

  const badge = getRoleBadge()

  return (
    <div className="min-h-screen bg-[#D6e1ff] flex">
      {/* Left Side - White Card (55%) */}
      <div className="w-[55%] h-screen pt-8 pl-8 pb-8">
        <div className="bg-white rounded-2xl shadow-xl h-full flex flex-col">
          {/* Header with X button - Static */}
          <div className="flex items-start justify-between p-8 pb-6">
            <div>
              <h1 className="text-3xl font-semibold text-[#1e293b]">Manage role</h1>
              <p className="text-[#475569] font-regular text-base mt-1">Update role for a member</p>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={24} className="text-gray-500" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
            {/* Member Info Card */}
            <div className="border border-[gray-200] rounded-xl p-6 bg-white mb-10">
              <div className="flex items-start justify-between mb-8">
                <div className="flex items-center gap-4">
                  {selectedUser.profile_photo_url || selectedUser.avatar_url ? (
                    <img
                      src={selectedUser.profile_photo_url || selectedUser.avatar_url}
                      alt={selectedUser.name}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center">
                      <span className="text-white font-bold text-xl">
                        {selectedUser.name?.[0]?.toUpperCase() || '?'}
                      </span>
                    </div>
                  )}
                  <div>
                    <h2 className="text-2xl font-medium text-[#1e293b]">{selectedUser.name}</h2>
                    <p className="text-[#475569] text-base">{selectedUser.email}</p>
                  </div>
                </div>
                <span className={`px-4 py-1.5 rounded-full text-sm font-medium ${badge.color}`}>
                  {badge.text}
                </span>
              </div>

              {/* Organization Information */}
              <div className="rounded-xl p-6 bg-[#f1f5f9]">
                <h3 className="text-base font-medium text-[#475569] mb-3">Organisation information</h3>
                <div className="space-y-2">
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-[#1e293b] text-sm">Team</span>
                    <span className="text-[#1e293b] font-medium text-sm">{currentHierarchy.team}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-[#1e293b] text-sm">Sub team</span>
                    <span className="text-[#1e293b] font-medium text-sm">{currentHierarchy.group}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-[#1e293b] text-sm">Department</span>
                    <span className="text-[#1e293b] font-medium text-sm">{currentHierarchy.subgroup}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Role Selection */}
            <div>
              <label className="block text-base font-medium text-[#1e293b] mb-2">
                What new role do you want to assign {selectedUser.name?.split(' ')[0]}?
              </label>
              <div className="flex flex-wrap gap-2">
                {['lead', 'head', 'hod', 'member', 'unassigned'].map((role) => (
                  <button
                    key={role}
                    onClick={() => {
                      setNewRole(role)
                      setSelectedTeam('')
                      setSelectedGroup('')
                      setSelectedSubgroup('')
                    }}
                    className={`px-6 py-2 rounded-full text-base font-medium transition-all ${
                      newRole === role
                        ? 'bg-[#eaf0ff] text-[#3167fe] border border-[#3167fe]'
                        : 'bg-[#f8fafc] text-[#64748b] border border-[#e2e8f0]'
                    }`}
                  >
                    {role === 'lead' && 'Lead'}
                    {role === 'head' && 'Head'}
                    {role === 'hod' && 'H.O.D'}
                    {role === 'member' && 'Member'}
                    {role === 'unassigned' && 'Unassigned/New member'}
                  </button>
                ))}
              </div>
            </div>

            {/* Hierarchy Selection */}
            {newRole && newRole !== 'unassigned' && (
              <div>
                <label className="block text-base font-medium text-[#1e293b] mb-2 mt-10">
                  {newRole === 'lead' && 'Select Team'}
                  {newRole === 'head' && 'Select Sub team'}
                  {(newRole === 'hod' || newRole === 'member') && 'Select Department'}
                </label>

                {/* Team Lead - Show Team only */}
                {newRole === 'lead' && (
                  <select
                    value={selectedTeam}
                    onChange={(e) => setSelectedTeam(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                )}

                {/* Group Leader - Show Group with auto-filled Team */}
                {newRole === 'head' && (
                  <div className="space-y-3">
                    <select
                      value={selectedGroup}
                      onChange={(e) => setSelectedGroup(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select</option>
                      {groups.map(group => (
                        <option key={group.id} value={group.id}>{group.name}</option>
                      ))}
                    </select>

                    {selectedGroup && (
                      <div>
                        <label className="block text-sm text-gray-500 mb-1">Team (auto-filled)</label>
                        <input
                          type="text"
                          value={getSelectedTeamName()}
                          disabled
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Subgroup Leader or Member - Show Subgroup with auto-filled Group and Team */}
                {(newRole === 'hod' || newRole === 'member') && (
                  <div className="space-y-3">
                    <select
                      value={selectedSubgroup}
                      onChange={(e) => setSelectedSubgroup(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select </option>
                      {subgroups.map(subgroup => (
                        <option key={subgroup.id} value={subgroup.id}>{subgroup.name}</option>
                      ))}
                    </select>

                    {selectedSubgroup && (
                      <>
                        <div>
                          <label className="block text-sm text-gray-500 mb-1">Sub team (auto-filled)</label>
                          <input
                            type="text"
                            value={getSelectedGroupName()}
                            disabled
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-500 mb-1">Team (auto-filled)</label>
                          <input
                            type="text"
                            value={getSelectedTeamName()}
                            disabled
                            className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-gray-50 text-gray-600"
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Summary */}
            {newRole && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-base font-medium text-[#475569] mb-2">Summary</h3>
                <p className="text-[#1e293b]">
                  You are assigning <strong>{selectedUser.name}</strong> as a{' '}
                  <span className="text-[#3167fe] font-bold">{getRoleDisplayName()}</span>
                  {getAssignmentName() && (
                    <> for <strong>{getAssignmentName()}</strong></>
                  )}
                </p>
              </div>
            )}

            {/* Confirm Button */}
            <button
              onClick={handleRoleChange}
              disabled={loading || !newRole}
              className="w-40 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Updating...' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>

      {/* Right Side - Illustration & Text (45%) */}
      <div className="w-[45%] flex flex-col items-center justify-center px-12">
        {/* Illustration */}
        <div className="mb-8">
          <img 
            src="/role.png" 
            alt="Manage role illustration" 
            className="w-96 h-96 object-contain"
          />
        </div>

        {/* Did You Know Text */}
        <div className="text-center">
          <h2 className="text-[#2248b2] font-semibold text-sm mb-3">DID YOU KNOW...</h2>
          <p className="text-[#475569] text-xl leading-relaxed max-w-md font-bold">
            You're building strong <span className="text-blue-700 font-bold">leadership</span>! 
            Each role creates <span className="text-blue-700 font-bold">accountability</span> and 
            distributes oversight efficiently
          </p>
        </div>
      </div>
    </div>
  )
}